import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useAnimation,
} from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  getNovelFeatureSpoil,
  setNovelFeatureSpoil,
} from '@/utils/novelFeatureSpoil';
import { generateUUID } from '@/utils/uuid';
import { bookServiceApi } from '@/services/api';
import { UserAvatar } from './ChatAvatars';
import GemIcon from '@/components/icons/GemIcon';
import { getBookCharacters } from '@/features/library/api/characters';
import { analyticsService } from '@/services/analyticsService';
import SpoilerInfoTooltip from '@/components/ui/SpoilerInfoTooltip';
import SpoilAccessSwitch from './SpoilAccessSwitch';
import { useQueryClient } from '@tanstack/react-query';
import { appendChatHistoryCache, useChatHistoryQuery } from '@/features/reading/hooks/useChatHistory';
import { useAuth } from '@/features/authentication/contexts/AuthContext';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface NovelCharacterChatProps {
  open: boolean;
  onClose: () => void;
  bookId?: number;
  bgColor?: 'white' | 'sepia' | 'dark';
  maxHeightVh?: number;
  bottomOffset?: string;
  onThinkingChange?: (thinking: boolean) => void;
  onAssistantResponse?: () => void;
  onAssistantError?: (kind: 'insufficient_gems' | 'general') => void;
  analyticsBookTitle?: string;
  analyticsSectionId?: number;
  analyticsFlow?: 'onboarding' | 'normal';
  canDisableSpoil?: boolean;
  analyticsPageContext?: string;
  disableDismiss?: boolean;
}

// Default suggestion chips for character chat
const defaultSuggestions = ['سلام.چه طوری ؟', 'حالت چه طوره؟', 'چه خبر؟'];

const NovelCharacterChat = ({
  open,
  onClose,
  bookId,
  bgColor = 'white',
  maxHeightVh = 84,
  bottomOffset,
  onThinkingChange,
  onAssistantResponse,
  onAssistantError,
  analyticsBookTitle,
  analyticsSectionId,
  analyticsFlow = 'normal',
  canDisableSpoil,
  analyticsPageContext = 'novel_read',
  disableDismiss = false,
}: NovelCharacterChatProps) => {
  const bgColorClasses = {
    white: 'bg-gray-50',
    sepia: 'bg-[#f4ecd8]',
    dark: 'bg-gray-900',
  };

  const textColorClasses = {
    white: 'text-gray-800',
    sepia: 'text-[#5c4a2f]',
    dark: 'text-gray-100',
  };

  const dragHandleClasses = {
    white: 'bg-gray-300',
    sepia: 'bg-[#c4b89a]',
    dark: 'bg-gray-600',
  };

  const [messagesByCharacter, setMessagesByCharacter] = useState<
    Record<number, ChatMessage[]>
  >({});
  const { user } = useAuth();
  const historyUserId = user?.id ?? 'anonymous';
  const queryClient = useQueryClient();
  const hydratedHistoryKeysRef = useRef(new Set<string>());
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState<number | null>(
    null
  );
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(
    null
  );
  const historyQuery = useChatHistoryQuery('character', selectedCharacterId ?? undefined, open, historyUserId);
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [isSpoil, setIsSpoil] = useState(() =>
    analyticsFlow === 'onboarding' || getNovelFeatureSpoil(bookId, 'character-chat')
  );

  useEffect(() => {
    setIsSpoil(
      analyticsFlow === 'onboarding' ||
        getNovelFeatureSpoil(bookId, 'character-chat')
    );
  }, [analyticsFlow, bookId]);

  const handleSpoilChange = useCallback((next: boolean) => {
    setIsSpoil(next);
    if (analyticsFlow !== 'onboarding') {
      setNovelFeatureSpoil(bookId, 'character-chat', next);
    }
  }, [analyticsFlow, bookId]);

  useEffect(() => {
    if (analyticsFlow !== 'onboarding' && canDisableSpoil === false) {
      setIsSpoil(false);
      setNovelFeatureSpoil(bookId, 'character-chat', false);
    }
  }, [analyticsFlow, bookId, canDisableSpoil]);

  const listRef = useRef<HTMLDivElement | null>(null);
  const charListRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const thinkingMsgIdRef = useRef<string | null>(null);
  const selectedCharacterIdRef = useRef<number | null>(null);

  const toPersianDigits = useCallback((num: number | string): string => {
    const persianDigits = [
      '\u06F0',
      '\u06F1',
      '\u06F2',
      '\u06F3',
      '\u06F4',
      '\u06F5',
      '\u06F6',
      '\u06F7',
      '\u06F8',
      '\u06F9',
    ];
    return String(num).replace(/[0-9]/g, d => persianDigits[parseInt(d, 10)]);
  }, []);

  const y = useMotionValue(0);
  const controls = useAnimation();
  const sheetScale = useTransform(y, [0, 400], [1, 0.92]);
  const borderRadius = useTransform(y, [0, 100], [24, 32]);

  const selectedCharacterName = useMemo(
    () =>
      characters.find(c => c.character_id === selectedCharacterId)?.name ||
      'کاراکتر',
    [characters, selectedCharacterId]
  );

  const selectedCharacter = useMemo(
    () => characters.find(c => c.character_id === selectedCharacterId) || null,
    [characters, selectedCharacterId]
  );

  useEffect(() => {
    if (!open || !selectedCharacterId || historyQuery.isLoading) return;
    const key = `${historyUserId}:character:${selectedCharacterId}`;
    if (hydratedHistoryKeysRef.current.has(key)) return;
    hydratedHistoryKeysRef.current.add(key);
    const turns = historyQuery.data?.items ?? [];
    setMessagesByCharacter(previous => ({
      ...previous,
      [selectedCharacterId]: turns.flatMap(turn => {
        const timestamp = turn.created_at ? new Date(turn.created_at).getTime() : Date.now();
        return [
          { id: `${turn.chat_id}-user`, role: 'user' as const, content: turn.user_message, timestamp },
          { id: `${turn.chat_id}-assistant`, role: 'assistant' as const, content: turn.response, timestamp: timestamp + 1 },
        ];
      }),
    }));
  }, [open, selectedCharacterId, historyUserId, historyQuery.data, historyQuery.isLoading]);

  const messages = useMemo(
    () =>
      selectedCharacterId ? messagesByCharacter[selectedCharacterId] || [] : [],
    [messagesByCharacter, selectedCharacterId]
  );

  const centerCharacter = useCallback(
    (id: number, behavior: ScrollBehavior = 'smooth') => {
      const container = charListRef.current;
      if (!container) return;
      const target = container.querySelector<HTMLElement>(
        `[data-character-id="${id}"]`
      );
      if (!target) return;

      isProgrammaticScrollRef.current = true;
      target.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });

      if (scrollTimeoutRef.current)
        window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    },
    [characters]
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messagesByCharacter, selectedCharacterId, open]);

  useEffect(() => {
    if (open) {
      y.set(window.innerHeight);
      controls.start({
        y: 0,
        transition: {
          type: 'spring',
          damping: 30,
          stiffness: 400,
        },
      });
    }
  }, [open, controls, y]);

  useEffect(() => {
    if (open && selectedCharacterId) {
      // Only center once when opening
      setTimeout(() => centerCharacter(selectedCharacterId, 'auto'), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, centerCharacter]);

  useEffect(() => {
    if (!open || !bookId) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingCharacters(true);
        const res = await getBookCharacters(bookId);
        if (!mounted) return;
        const items = res.characters || [];
        setCharacters(items);
        const initialId = items[0]?.character_id ?? null;
        setSelectedCharacterId(prev => prev ?? initialId);
        if (initialId) {
          setTimeout(() => centerCharacter(initialId, 'auto'), 100);
        }
      } catch (e) {
        console.error('Failed to load novel characters', e);
        if (mounted) {
          setCharacters([]);
          setSelectedCharacterId(null);
        }
      } finally {
        if (mounted) setLoadingCharacters(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookId]);

  useEffect(() => {
    selectedCharacterIdRef.current = selectedCharacterId;
  }, [selectedCharacterId]);

  useEffect(() => {
    const container = charListRef.current;
    if (!open || !container || characters.length === 0) return;

    let scrollTimeout: number;
    let rafId: number | null = null;

    const updateFocusedCharacter = () => {
      const items = Array.from(
        container.querySelectorAll<HTMLElement>('[data-character-id]')
      );
      if (items.length === 0) return;

      const containerCenter = container.scrollLeft + container.clientWidth / 2;
      let closestCharacterId: number | null = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach(item => {
        const rawId = item.getAttribute('data-character-id');
        if (!rawId) return;
        const characterId = Number(rawId);
        if (Number.isNaN(characterId)) return;

        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(itemCenter - containerCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestCharacterId = characterId;
        }
      });

      if (
        closestCharacterId !== null &&
        closestCharacterId !== selectedCharacterIdRef.current
      ) {
        setSelectedCharacterId(closestCharacterId);
      }
    };

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        window.clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 150);
        return;
      }

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateFocusedCharacter);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
      window.clearTimeout(scrollTimeout);
    };
  }, [open, characters]);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const shouldClose = info.offset.y > 100 || info.velocity.y > 300;
      if (shouldClose) {
        onClose();
      } else {
        controls.start({
          y: 0,
          transition: {
            type: 'spring',
            damping: 25,
            stiffness: 500,
          },
        });
      }
    },
    [onClose, controls]
  );

  const handleSend = async (messageText?: string) => {
    const trimmed = (messageText || input).trim();
    if (!trimmed || !selectedCharacterId) return;

    const analyticsParams = {
      chat_type: 'character',
      message_source: messageText ? 'suggestion' : 'typed',
      book_id: bookId ? String(bookId) : 'unknown',
      book_title: analyticsBookTitle || 'unknown',
      ...(analyticsSectionId ? { section_id: String(analyticsSectionId) } : {}),
      page_context: analyticsPageContext,
      flow: analyticsFlow,
    };
    void analyticsService.logEvent('chat_message_sent', analyticsParams);

    const userMsg: ChatMessage = {
      id: generateUUID(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };
    setMessagesByCharacter(prev => ({
      ...prev,
      [selectedCharacterId]: [...(prev[selectedCharacterId] || []), userMsg],
    }));
    setInput('');

    setIsThinking(true);
    onThinkingChange?.(true);
    const thinkingId = generateUUID();
    thinkingMsgIdRef.current = thinkingId;

    setMessagesByCharacter(prev => ({
      ...prev,
      [selectedCharacterId]: [
        ...(prev[selectedCharacterId] || []),
        {
          id: thinkingId,
          role: 'assistant',
          content: '',
          timestamp: Date.now() + 1,
        },
      ],
    }));

    try {
      const res = await bookServiceApi.post<{
        answer?: string;
        detail?: string;
        is_premium?: boolean;
        remaining_requests?: number;
        gems_info?: {
          previous_gems: number;
          new_gems: number;
          gems_used: boolean;
        };
      }>(`/novel/chatbot/${selectedCharacterId}?is_spoil=${isSpoil}`, {
        message: trimmed,
      });

      if (res.data?.remaining_requests !== undefined) {
        setRemainingRequests(res.data.remaining_requests);
      }

      if (res.data?.detail) {
        throw new Error(res.data.detail);
      }

      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to get response');
      }

      const answer = res.data.answer || 'متاسفانه پاسخی دریافت نشد.';
      const persianAnswer = toPersianDigits(answer);
      setMessagesByCharacter(prev => ({
        ...prev,
        [selectedCharacterId]: (prev[selectedCharacterId] || []).map(m =>
          m.id === thinkingId
            ? { ...m, content: persianAnswer, timestamp: Date.now() }
            : m
        ),
      }));
      appendChatHistoryCache(queryClient, historyUserId, 'character', selectedCharacterId, trimmed, persianAnswer);
      onAssistantResponse?.();
      void analyticsService.logEvent('chat_response_received', analyticsParams);
    } catch (error) {
      console.error('Novel character chat error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'متاسفانه خطایی رخ داد.';
      const normalized = errorMessage.toLowerCase();
      const isGemsError =
        normalized.includes('gem') ||
        normalized.includes('gems') ||
        normalized.includes('الماس') ||
        normalized.includes('جم') ||
        normalized.includes('insufficient');
      const persianErrorMessage = toPersianDigits(errorMessage);
      setMessagesByCharacter(prev => ({
        ...prev,
        [selectedCharacterId]: (prev[selectedCharacterId] || []).map(m =>
          m.id === thinkingId
            ? { ...m, content: persianErrorMessage, timestamp: Date.now() }
            : m
        ),
      }));
      onAssistantError?.(isGemsError ? 'insufficient_gems' : 'general');
    } finally {
      setIsThinking(false);
      onThinkingChange?.(false);
      thinkingMsgIdRef.current = null;
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSend(suggestion);
  };

  const isEmptyState = messages.length === 0;

  return (
      <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="novel-chat-fullscreen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[70]"
        >
          <motion.div
            className="absolute inset-0 bg-black/35 backdrop-blur-[2px]"
            onClick={disableDismiss ? undefined : onClose}
          />

          <motion.div
            drag={disableDismiss ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.6 }}
            onDragEnd={handleDragEnd}
            animate={controls}
            initial={false}
            exit={{
              y: '100%',
              transition: {
                type: 'spring',
                damping: 30,
                stiffness: 300,
              },
            }}
            style={{
              y,
              scale: sheetScale,
              borderTopLeftRadius: borderRadius,
              borderTopRightRadius: borderRadius,
              height: bottomOffset
                ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}vh`,
              maxHeight: bottomOffset
                ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}vh`,
              paddingBottom: bottomOffset || undefined,
            }}
            className={cn(
              'absolute inset-x-0 bottom-0',
              bgColorClasses[bgColor],
              'flex flex-col overflow-hidden shadow-2xl'
            )}
            dir="rtl"
          >
            <div className="flex justify-center pt-1.5 pb-1">
              <div
                className={cn(
                  'w-10 h-1 rounded-full',
                  dragHandleClasses[bgColor]
                )}
              />
            </div>

            <div
              className={cn(bottomOffset ? 'px-3 pt-1 pb-0' : 'px-4 pt-2 pb-1')}
            >
              <div
                ref={charListRef}
                className="overflow-x-auto hide-scrollbar snap-x snap-mandatory touch-pan-x"
                dir="ltr"
                onPointerDownCapture={e => e.stopPropagation()}
                onTouchStartCapture={e => e.stopPropagation()}
              >
                <div
                  className={cn(
                    'flex gap-0 min-w-max',
                    bottomOffset ? 'pt-2 pb-0' : 'pt-4 pb-1'
                  )}
                >
                  {/* Spacers for centering */}
                  <div
                    style={{ width: 'calc(50% - 48px)' }}
                    className="flex-shrink-0"
                  />

                  {loadingCharacters &&
                    Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={`char-skeleton-${idx}`}
                        className="flex flex-col items-center w-[96px] flex-shrink-0"
                      >
                        <div
                          className={cn(
                            bottomOffset
                              ? 'w-[84px] h-[84px]'
                              : 'w-[72px] h-[72px]',
                            'rounded-full shimmer-skeleton',
                            bgColor === 'dark'
                              ? 'bg-gray-700/70'
                              : 'bg-gray-300/80'
                          )}
                        />
                        <div
                          className={cn(
                            'mt-1 h-3 w-12 rounded shimmer-skeleton',
                            bgColor === 'dark'
                              ? 'bg-gray-700/70'
                              : 'bg-gray-300/80'
                          )}
                        />
                      </div>
                    ))}

                  {!loadingCharacters &&
                    characters.map(character => {
                      const isSelected =
                        character.character_id === selectedCharacterId;
                      return (
                        <button
                          key={character.character_id}
                          data-character-id={character.character_id}
                          type="button"
                          onClick={() => {
                            setSelectedCharacterId(character.character_id);
                            centerCharacter(character.character_id);
                          }}
                          className={`relative flex w-[96px] snap-center flex-shrink-0 flex-col items-center transition-all duration-300 ${
                            isSelected
                              ? bottomOffset
                                ? 'z-20 scale-105 opacity-100'
                                : 'z-20 -translate-y-2 scale-110 opacity-100'
                              : 'z-0 scale-[0.82] opacity-45'
                          }`}
                          style={{ zIndex: isSelected ? 2 : 1 }}
                        >
                          <div
                            className={cn(
                              bottomOffset
                                ? 'w-[84px] h-[84px]'
                                : 'w-[72px] h-[72px]',
                              'rounded-full transition-all flex items-center justify-center p-0.5 relative',
                              isSelected
                                ? bgColor === 'sepia'
                                  ? 'border-[#8a5d28] shadow-lg border-[3px] bg-white'
                                  : 'border-primary-500 shadow-lg border-[3px] bg-white'
                                : bgColor === 'dark'
                                  ? 'border-gray-600 border-2'
                                  : 'border-[#e0deff] border-2'
                            )}
                          >
                            <div className="w-full h-full rounded-full overflow-hidden">
                              <img
                                src={character.icon}
                                alt={character.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          </div>
                          <span
                            className={cn(
                              bottomOffset
                                ? 'mt-2.5 text-xs'
                                : 'mt-2 text-[11px]',
                              'text-center font-persian leading-4 line-clamp-2 px-1',
                              isSelected
                                ? bgColor === 'dark'
                                  ? 'text-white font-bold'
                                  : 'text-[#2f2b63] font-bold'
                                : bgColor === 'dark'
                                  ? 'text-gray-400'
                                  : 'text-[#5f5a9a]'
                            )}
                          >
                            {character.name}
                          </span>
                        </button>
                      );
                    })}

                  <div
                    style={{ width: 'calc(50% - 48px)' }}
                    className="flex-shrink-0"
                  />
                </div>
              </div>
            </div>

            <div
              className={cn(
                'flex items-center justify-center gap-2 px-4',
                bottomOffset
                  ? analyticsFlow === 'onboarding'
                    ? 'pb-1 pt-3'
                    : 'pb-1 pt-0'
                  : 'pb-1 pt-1'
              )}
              dir="rtl"
            >
              <div className="relative">
                <span
                  className={cn(
                    'font-persian text-xs font-semibold',
                    bgColor === 'dark'
                      ? 'text-gray-100'
                      : bgColor === 'sepia'
                        ? 'text-[#5c4a2f]'
                        : 'text-gray-700'
                  )}
                >
                  لو نده
                </span>
                <SpoilerInfoTooltip
                  description="روشن بودن یعنی جواب شخصیت فقط بر اساس قسمت هایی هست که خوندی.با خاموش کردن جواب شخصیت بر اساس کل قسمت های کتاب هست. حتی اون قسمت هایی که نخوندی"
                  className={cn(
                    'absolute',
                    analyticsFlow === 'onboarding'
                      ? '-top-1 -right-3 scale-75'
                      : '-top-3 -right-1'
                  )}
                />
              </div>
              <SpoilAccessSwitch
                label=""
                isSpoil={isSpoil}
                canDisableSpoil={analyticsFlow === 'onboarding' || canDisableSpoil === true}
                onSpoilChange={handleSpoilChange}
                theme={bgColor}
                bookId={bookId}
                bookTitle={analyticsBookTitle}
                subscriptionButtonType="character_chat"
              />
            </div>

            <div
              ref={listRef}
              className={cn(
                'flex-1 overflow-y-auto hide-scrollbar',
                bottomOffset ? 'px-3 pt-0 pb-2' : 'px-4 pt-1 pb-4'
              )}
            >
              {historyQuery.isLoading && selectedCharacterId ? (
                <div className="flex h-full flex-col items-center justify-center gap-3" aria-live="polite">
                  <div className="h-8 w-8 rounded-full border-2 border-primary-500/30 border-t-primary-500 animate-spin" />
                  <p className={cn('font-persian text-sm', textColorClasses[bgColor])}>در حال بارگذاری تاریخچه گفتگو…</p>
                </div>
              ) : historyQuery.isError && selectedCharacterId && messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <p className={cn('font-persian text-sm', textColorClasses[bgColor])}>بارگذاری تاریخچه گفتگو انجام نشد.</p>
                  <button type="button" onClick={() => void historyQuery.refetch()} className="font-persian text-sm text-primary-600 underline">تلاش دوباره</button>
                </div>
              ) : isEmptyState ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  {loadingCharacters || !selectedCharacter ? (
                    <>
                      {loadingCharacters ? (
                        <>
                          <div
                            className={cn(
                              'w-20 h-20 rounded-full shimmer-skeleton',
                              bgColor === 'dark'
                                ? 'bg-gray-700/70'
                                : 'bg-gray-300/80'
                            )}
                          />
                          <div className="text-center">
                            <div
                              className={cn(
                                'mx-auto h-6 w-44 rounded shimmer-skeleton mb-2',
                                bgColor === 'dark'
                                  ? 'bg-gray-700/70'
                                  : 'bg-gray-300/80'
                              )}
                            />
                            <div
                              className={cn(
                                'mx-auto h-4 w-40 rounded shimmer-skeleton',
                                bgColor === 'dark'
                                  ? 'bg-gray-700/70'
                                  : 'bg-gray-300/80'
                              )}
                            />
                          </div>
                        </>
                      ) : (
                        <p
                          className={cn(
                            'flex items-center justify-center gap-0.5 font-persian text-lg font-bold',
                            bgColor === 'dark'
                              ? 'text-gray-300'
                              : 'text-gray-500'
                          )}
                          dir="rtl"
                        >
                          <span>به زودی</span>
                          {[0, 1, 2].map(index => (
                            <motion.span
                              key={index}
                              animate={{ opacity: [0, 1, 0] }}
                              transition={{
                                duration: 1.4,
                                repeat: Infinity,
                                delay: index * 0.25,
                              }}
                            >
                              .
                            </motion.span>
                          ))}
                        </p>
                      )}
                    </>
                  ) : bottomOffset ? null : (
                    <>
                      <img
                        src={selectedCharacter.icon}
                        alt={selectedCharacter.name}
                        className={cn(
                          'rounded-full object-cover border-4 border-white/40 shadow-xl',
                          bottomOffset ? 'w-[72px] h-[72px]' : 'w-20 h-20'
                        )}
                      />
                      <div className="text-center">
                        <h2
                          className={cn(
                            bottomOffset ? 'text-base' : 'text-lg',
                            'font-semibold font-persian mb-1',
                            textColorClasses[bgColor]
                          )}
                        >
                          من {selectedCharacterName} هستم
                        </h2>
                        <p
                          className={cn(
                            bottomOffset ? 'text-[13px]' : 'text-sm',
                            'font-persian',
                            bgColor === 'dark' ? 'text-gray-400' : 'text-muted'
                          )}
                        >
                          با {selectedCharacterName} حرف بزن
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {messages.map(m => (
                    <motion.div
                      key={m.id}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className={cn(
                        'flex items-end gap-2',
                        m.role === 'user' ? 'flex-row' : 'flex-row-reverse'
                      )}
                    >
                      {m.role === 'user' ? (
                        <UserAvatar size="md" />
                      ) : selectedCharacter?.icon ? (
                        <img
                          src={selectedCharacter.icon}
                          alt={selectedCharacter.name}
                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gray-300 flex-shrink-0" />
                      )}
                      <div
                        className={cn(
                          'relative px-4 py-3 rounded-2xl text-[13px] leading-relaxed font-persian whitespace-pre-wrap max-w-[80%]',
                          m.role === 'user'
                            ? cn(
                                'rounded-br-md',
                                bgColor === 'dark'
                                  ? 'bg-gray-800 text-gray-100 border border-gray-700'
                                  : bgColor === 'sepia'
                                    ? 'bg-[#e5dcc3] text-[#5c4a2f] border border-[#d4c4a8]'
                                    : 'bg-soft-neutral-200 text-foreground'
                              )
                            : cn(
                                'rounded-bl-md shadow-sm',
                                bgColor === 'dark'
                                  ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white'
                                  : bgColor === 'sepia'
                                    ? 'bg-gradient-to-br from-[#8B5A2B] to-[#6B4423] text-[#f4ecd8]'
                                    : 'bg-gradient-to-br from-primary-500 to-primary-600 text-white'
                              ),
                          m.id === thinkingMsgIdRef.current && 'animate-pulse'
                        )}
                      >
                        {m.id === thinkingMsgIdRef.current ? (
                          <div className="flex items-center gap-1 py-1 px-0.5">
                            <span className="typing-dot" />
                            <span
                              className="typing-dot"
                              style={{ animationDelay: '0.16s' }}
                            />
                            <span
                              className="typing-dot"
                              style={{ animationDelay: '0.32s' }}
                            />
                          </div>
                        ) : (
                          m.content
                        )}
                        <span
                          className={cn(
                            'absolute w-3 h-3 rotate-45',
                            m.role === 'user'
                              ? bgColor === 'dark'
                                ? 'right-[-6px] bottom-3 bg-gray-800 border-r border-b border-gray-700'
                                : bgColor === 'sepia'
                                  ? 'right-[-6px] bottom-3 bg-[#e5dcc3] border-r border-b border-[#d4c4a8]'
                                  : 'right-[-6px] bottom-3 bg-soft-neutral-200'
                              : bgColor === 'dark'
                                ? 'left-[-6px] bottom-3 bg-purple-700'
                                : bgColor === 'sepia'
                                  ? 'left-[-6px] bottom-3 bg-[#7a4d26]'
                                  : 'left-[-6px] bottom-3 bg-primary-600'
                          )}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Suggestion Chips - Only show when empty and not thinking */}
            {isEmptyState &&
              !isThinking &&
              !loadingCharacters &&
              selectedCharacter && (
                <div
                  className="px-4 py-2 overflow-x-auto"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <style>{`
                  .suggestion-chips::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                  <div
                    className="flex gap-2 suggestion-chips"
                    style={{ direction: 'rtl' }}
                  >
                    {defaultSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={cn(
                          'px-4 py-2 rounded-full text-sm font-persian whitespace-nowrap',
                          'active:scale-95 transition-all duration-200',
                          bgColor === 'dark'
                            ? 'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700'
                            : bgColor === 'sepia'
                              ? 'bg-[#e5dcc3] text-[#5c4a2f] border border-[#d4c4a8] hover:bg-[#d9cdb4]'
                              : 'bg-soft-neutral-100 text-foreground border border-soft-neutral-200 hover:bg-soft-neutral-200'
                        )}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {remainingRequests !== null && (
              <div
                className={cn(
                  'px-4 py-2 text-center text-xs font-persian',
                  bgColor === 'dark'
                    ? 'text-gray-400'
                    : bgColor === 'sepia'
                      ? 'text-[#8a7a5a]'
                      : 'text-gray-500'
                )}
              >
                <span>
                  تعداد درخواست‌های باقی‌مانده برای امروز:{' '}
                  {toPersianDigits(remainingRequests)}
                </span>
              </div>
            )}

            <form
              className={cn(
                'px-4 py-3 border-t flex flex-row-reverse gap-2',
                bgColor === 'dark'
                  ? 'border-gray-700 bg-gray-900'
                  : bgColor === 'sepia'
                    ? 'border-[#d4c4a8] bg-[#f4ecd8]'
                    : 'border-soft-neutral-200 bg-gray-50'
              )}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              onSubmit={e => {
                e.preventDefault();
                handleSend();
              }}
            >
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={`با ${selectedCharacterName} حرف بزن`}
                disabled={isThinking || !selectedCharacterId}
                className={cn(
                  bottomOffset
                    ? 'flex-1 h-12 px-4 rounded-2xl text-sm font-persian'
                    : 'flex-1 h-11 px-4 rounded-2xl text-sm font-persian',
                  'focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200',
                  bgColor === 'dark'
                    ? 'bg-gray-800 border border-gray-700 text-white placeholder:text-gray-500'
                    : bgColor === 'sepia'
                      ? 'bg-[#e5dcc3] border border-[#d4c4a8] text-[#2d2416] placeholder:text-[#8a7a5a]'
                      : 'bg-gray-100 border border-transparent text-gray-900 placeholder:text-gray-400 focus:bg-white'
                )}
                dir="rtl"
              />
              <button
                type="submit"
                disabled={!input.trim() || isThinking || !selectedCharacterId}
                className={cn(
                  bottomOffset
                    ? 'flex items-center justify-center w-12 h-12 rounded-full'
                    : 'flex items-center justify-center w-11 h-11 rounded-full',
                  'shadow-sm flex-shrink-0 text-white disabled:bg-slate-500 disabled:text-white disabled:cursor-not-allowed transition-transform active:scale-95',
                  !input.trim() || isThinking || !selectedCharacterId
                    ? 'bg-gray-300 text-gray-500'
                    : bgColor === 'dark'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : bgColor === 'sepia'
                        ? 'bg-[#8B5A2B] hover:bg-[#6B4423]'
                        : 'bg-primary-500 hover:bg-primary-600'
                )}
                aria-label="ارسال پیام"
              >
                {remainingRequests !== null && remainingRequests === 0 ? (
                  <div
                    className="flex items-center justify-center gap-0.5"
                    dir="rtl"
                  >
                    <span className="text-sm font-bold text-white">۲۰</span>
                    <GemIcon size={12} color="flatWhite" className="flex-shrink-0 -translate-y-px" />
                  </div>
                ) : (
                  <ArrowUp className="w-5 h-5" />
                )}
              </button>
            </form>

            <style>{`
              .shimmer-skeleton {
                position: relative;
                overflow: hidden;
              }
              .shimmer-skeleton::after {
                content: '';
                position: absolute;
                inset: 0;
                transform: translateX(-100%);
                background: linear-gradient(
                  90deg,
                  rgba(255,255,255,0) 0%,
                  rgba(255,255,255,0.35) 50%,
                  rgba(255,255,255,0) 100%
                );
                animation: skeletonShimmer 1.2s infinite;
              }
              @keyframes skeletonShimmer {
                100% { transform: translateX(100%); }
              }
              .typing-dot {
                width: 7px;
                height: 7px;
                border-radius: 9999px;
                background: rgba(255,255,255,0.92);
                animation: typingBounce 0.9s infinite ease-in-out;
                display: inline-block;
              }
              @keyframes typingBounce {
                0%, 70%, 100% { transform: translateY(0); opacity: 0.35; }
                35% { transform: translateY(-5px); opacity: 1; }
              }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NovelCharacterChat;
